import { useEffect, useMemo, useState } from 'react';
import { FormInstance } from 'antd/lib/form';
import {
  ExternalTalentFormValues,
  getBirthDayCount,
} from 'helpers/externalTalents';

type UseBirthDateFieldsOptions = {
  form: FormInstance<ExternalTalentFormValues>;
  initialValues: ExternalTalentFormValues;
  isOpen: boolean;
};

type UseBirthDateFieldsResult = {
  birthDateOptions: number[];
  handleBirthMonthChange: (value?: number) => void;
  handleBirthYearChange: (value?: number) => void;
  selectedBirthMonth: number | null;
  selectedBirthYear: number | null;
};

export const useBirthDateFields = ({
  form,
  initialValues,
  isOpen,
}: UseBirthDateFieldsOptions): UseBirthDateFieldsResult => {
  const [selectedBirthYear, setSelectedBirthYear] = useState<number | null>(
    null
  );
  const [selectedBirthMonth, setSelectedBirthMonth] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedBirthYear(initialValues.birthYear || null);
    setSelectedBirthMonth(initialValues.birthMonth || null);
  }, [initialValues.birthMonth, initialValues.birthYear, isOpen]);

  const birthDateOptions = useMemo(
    () =>
      Array.from(
        { length: getBirthDayCount(selectedBirthYear, selectedBirthMonth) },
        (_, index) => index + 1
      ),
    [selectedBirthMonth, selectedBirthYear]
  );

  const handleBirthYearChange = (value?: number) => {
    setSelectedBirthYear(value || null);

    if (!value) {
      setSelectedBirthMonth(null);
      form.setFieldsValue({
        birthDate: null,
        birthMonth: null,
        birthYear: null,
      });
      return;
    }

    const currentBirthMonth = form.getFieldValue('birthMonth');
    const currentBirthDate = form.getFieldValue('birthDate');

    if (!currentBirthMonth) {
      form.setFieldsValue({ birthDate: null, birthYear: value });
      return;
    }

    const maxBirthDate = getBirthDayCount(value, currentBirthMonth);

    form.setFieldsValue({
      birthDate:
        currentBirthDate && currentBirthDate <= maxBirthDate
          ? currentBirthDate
          : null,
      birthYear: value,
    });
  };

  const handleBirthMonthChange = (value?: number) => {
    setSelectedBirthMonth(value || null);

    if (!value) {
      form.setFieldsValue({
        birthDate: null,
        birthMonth: null,
      });
      return;
    }

    const currentBirthDate = form.getFieldValue('birthDate');
    const maxBirthDate = getBirthDayCount(selectedBirthYear, value);

    form.setFieldsValue({
      birthDate:
        currentBirthDate && currentBirthDate <= maxBirthDate
          ? currentBirthDate
          : null,
      birthMonth: value,
    });
  };

  return {
    birthDateOptions,
    handleBirthMonthChange,
    handleBirthYearChange,
    selectedBirthMonth,
    selectedBirthYear,
  };
};
